// Duty-home session progress — pure model (ship-dark behind the web_duty_home flag).
//
// HONESTY SCOPE: this is "captured THIS SESSION" — it resets when the app reloads. It is a
// guard's working memory ("which ones have I hit since I opened the app"), not the server's
// shift record; the supervisor console remains the source of truth. The panel says so on its
// face. Server-backed shift progress is a later slice once a guard-readable own-scans contract
// exists (mirrors mobile's patrol-home approach).

export interface DutyCheckpoint {
  checkpointId: string;
  label: string;
}

/** What a finished capture tells us — manual knows the id; QR/NFC know the server-resolved name. */
export interface CapturedMark {
  checkpointId: string | null;
  checkpointName: string | null;
}

/** Resolve a capture to a checkpoint id and add it to the set. Unresolvable marks (e.g. an
 *  offline QR capture whose checkpoint the server hasn't named yet) leave the set unchanged —
 *  the progress only ever claims what it can prove. */
export function markCaptured(
  captured: ReadonlySet<string>,
  checkpoints: readonly DutyCheckpoint[],
  mark: CapturedMark,
): ReadonlySet<string> {
  let resolved: string | null = null;
  if (mark.checkpointId !== null && checkpoints.some((cp) => cp.checkpointId === mark.checkpointId)) {
    resolved = mark.checkpointId;
  } else if (mark.checkpointName !== null) {
    const byLabel = checkpoints.find((cp) => cp.label === mark.checkpointName);
    resolved = byLabel?.checkpointId ?? null;
  }
  if (resolved === null || captured.has(resolved)) {
    return captured;
  }
  const next = new Set(captured);
  next.add(resolved);
  return next;
}

export interface DutyProgressRow {
  checkpointId: string;
  label: string;
  captured: boolean;
}

export interface DutyProgressModel {
  total: number;
  captured: number;
  complete: boolean;
  headline: string;
  rows: DutyProgressRow[];
}

export function dutyProgress(
  checkpoints: readonly DutyCheckpoint[],
  captured: ReadonlySet<string>,
): DutyProgressModel {
  const rows = checkpoints.map((cp) => ({
    checkpointId: cp.checkpointId,
    label: cp.label,
    captured: captured.has(cp.checkpointId),
  }));
  const done = rows.filter((row) => row.captured).length;
  const total = rows.length;
  const complete = total > 0 && done === total;
  const headline =
    total === 0
      ? "No checkpoints loaded"
      : complete
        ? `All ${total} captured this session`
        : `${done} of ${total} this session`;
  return { total, captured: done, complete, headline, rows };
}
